/**
 * Inline Search - Simple dropdown search component for 404 page
 * Opens dropdown on focus/typing, closes on outside click
 * No overlay, no modal - just a simple dropdown panel
 */
class InlineSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isOpen = false;
    this.debounceTimer = null;
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.dropdown = this.querySelector('[data-inline-search-results]');
    this.resultsContainer = this.querySelector('[data-results-container]');
    this.loadingText = this.getAttribute('data-loading-text') || 'Loading...';

    if (!this.input || !this.dropdown) return;

    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    // Input events
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    // Form submit
    this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));

    // Outside click to close
    this.boundOnOutsideClick = this.onOutsideClick.bind(this);
    document.addEventListener('click', this.boundOnOutsideClick);

    // Escape key to close
    this.boundOnEscape = this.onEscape.bind(this);
    document.addEventListener('keydown', this.boundOnEscape);
  }

  removeEventListeners() {
    if (this.boundOnOutsideClick) {
      document.removeEventListener('click', this.boundOnOutsideClick);
    }
    if (this.boundOnEscape) {
      document.removeEventListener('keydown', this.boundOnEscape);
    }
  }

  onFocus() {
    const query = this.getQuery();
    if (query.length > 0) {
      // If there's already a query, show results or fetch new ones
      if (this.cachedResults[this.getCacheKey(query)]) {
        this.renderResults(this.cachedResults[this.getCacheKey(query)]);
        this.open();
      } else {
        this.fetchResults(query);
      }
    }
  }

  onInput() {
    clearTimeout(this.debounceTimer);

    const query = this.getQuery();

    if (query.length === 0) {
      this.close();
      this.searchTerm = '';
      return;
    }

    this.searchTerm = query;

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, 300);
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateResults('down');
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateResults('up');
        break;
      case 'Enter':
        const selected = this.querySelector('[aria-selected="true"] a');
        if (selected) {
          event.preventDefault();
          selected.click();
        }
        break;
    }
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length) {
      event.preventDefault();
      return;
    }

    // If an item is selected, navigate to it instead of submitting
    const selected = this.querySelector('[aria-selected="true"] a');
    if (selected) {
      event.preventDefault();
      selected.click();
    }
  }

  onOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  onEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.input.focus();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  getCacheKey(query) {
    return query.toLowerCase().replace(/\s+/g, '-');
  }

  fetchResults(query) {
    const cacheKey = this.getCacheKey(query);

    // Check cache first
    if (this.cachedResults[cacheKey]) {
      this.renderResults(this.cachedResults[cacheKey]);
      this.open();
      return;
    }

    // Abort previous request
    this.abortController.abort();
    this.abortController = new AbortController();

    // Show loading state
    this.setAttribute('loading', '');
    this.open();

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const resultsMarkup = doc.querySelector('#shopify-section-predictive-search')?.innerHTML || '';

        // Cache the results
        this.cachedResults[cacheKey] = resultsMarkup;

        this.renderResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          // Request was aborted, ignore
          return;
        }
        console.error('Search error:', error);
        this.close();
      })
      .finally(() => {
        this.removeAttribute('loading');
      });
  }

  renderResults(markup) {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = markup;
    }
    this.setAttribute('results', 'true');
    this.input.setAttribute('aria-expanded', 'true');
  }

  navigateResults(direction) {
    const items = Array.from(this.querySelectorAll('li[id], button.predictive-search__item')).filter(
      (el) => el.offsetParent !== null
    );

    if (items.length === 0) return;

    const currentSelected = this.querySelector('[aria-selected="true"]');
    let currentIndex = items.indexOf(currentSelected);

    // Remove current selection
    if (currentSelected) {
      currentSelected.setAttribute('aria-selected', 'false');
    }

    // Calculate new index
    if (direction === 'down') {
      currentIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    } else {
      currentIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    }

    // Set new selection
    const newSelected = items[currentIndex];
    if (newSelected) {
      newSelected.setAttribute('aria-selected', 'true');
      newSelected.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', newSelected.id || '');
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-activedescendant', '');

    // Clear selection
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) {
      selected.setAttribute('aria-selected', 'false');
    }
  }
}

customElements.define('inline-search', InlineSearch);
